import { FC, useState, useEffect, Fragment } from 'react';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Types
import QuizTypes from 'types/QuizTypes';
// Models
import IQuiz from 'models/Quiz';
import { IAnswerResult } from 'models/Answer';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { registerUser } from 'store/auth/authAsync';
import { createAnalyticsInteract, createResult, sendResult, updateAnalyticsData } from 'store/player/playerAsync';
// Selectors
import { selectTrackingAdvertClick } from 'store/ui/uiSelectors';
import { selectRegisterData, selectResultId } from 'store/auth/authSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectAnswers, selectBuckets, selectEntryTime, selectTotalScore } from 'store/player/playerSelectors';
// Components
import PublicDevicePlayable from 'components/PublicDevicePlayable';
import AppBar from 'components/AppBar';
import BannerImage from 'components/BannerImage';
import PublicForm from './PublicForm';
import AnonymousForm from './AnonymousForm';
import PoweredBy from 'components/PoweredBy';
// Services
import StorageService from 'services/Storage.service';
// Utilities
import { fetchWithAutoRetry, isProfanity } from 'utilities/Utilities';

const RegisterPage:FC = () => {
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);

  const resultId:string | null = useAppSelector(selectResultId);
  const entryTime:number | null = useAppSelector(selectEntryTime);
  const answers:IAnswerResult[] = useAppSelector(selectAnswers);
  const buckets:any = useAppSelector(selectBuckets);
  const totalScore:number = useAppSelector(selectTotalScore);
  // 
  const trackingAdvertClick:boolean = useAppSelector(selectTrackingAdvertClick)
  const userRegisterData = useAppSelector(selectRegisterData);

  const navigateTo = useNavigateTo();

  const [ loading, setLoading ] = useState<boolean>(false);
  const [ resultFetched, setResultFetched ] = useState<boolean>(false);

  useEffect(() => {
    if (!!quiz.registrationPage && quiz.registrationPage?.showPriorQuiz && !userRegisterData) return;

    if ( !resultId ){
      asyncCreateResult();
    } else {
      setResultFetched(true);
    }
    // eslint-disable-next-line
  }, []);

  const dispatchCreateResult = async () => {
    const data:any = {
      score: quiz.type === QuizTypes.Scoring ? totalScore : 0,
      namespace: quiz.clientNameSlug,
      name: quiz.quizNameSlug,
      nonce: quiz.nonce,
      answers
    };
    if ( quiz.type === QuizTypes.Personality ){
      data['buckets'] = Object.values(buckets);
    }
    const result = await dispatch(createResult(data)).unwrap();
    return result;
  }

  const dispatchUpdateAnalyticsData = async (data: any) => {
    return await dispatch(updateAnalyticsData(data)).unwrap();
  }

  const asyncCreateResult = async () => {
    try {
      const result:any = await fetchWithAutoRetry(dispatchCreateResult);
      await fetchWithAutoRetry(dispatchUpdateAnalyticsData, {
        result,
        entryTime,
        type: quiz.type,
        quizNameSlug: quiz.quizName
      });
      setResultFetched(true);
    } catch(error){}
  }

  const dispatchRegisterUser = async (registerData: any) => {
    return await dispatch(registerUser(registerData)).unwrap();
  }

  const asyncRegisterUser = async (
    {
      registerData = null,
      resultData = {},
      analyticData = null,
      togglePostQuizDialog = null,
    }:{
      registerData?:any,
      resultData?:any,
      analyticData?:any,
      togglePostQuizDialog?: ((() => void)) | null;
    }, doRedirect:boolean = true
  ) => {
    setLoading(true);
    try {
      if ( registerData ){
        await fetchWithAutoRetry(dispatchRegisterUser, { ...registerData, quizId: quiz._id });
        if ( trackingAdvertClick ){
          dispatch(createAnalyticsInteract({
            result: resultId,
            name: 'adClick'
          }));
        }
      }
      const nextResultData:any = {
        ...resultData,
        nonce: quiz.nonce
      }
      if (registerData && registerData.username && isProfanity(registerData.username)) {
        nextResultData['flagged'] = true;
      }
      if (registerData && !registerData.username && (isProfanity(registerData.name) || isProfanity(registerData.surname))) {
        nextResultData['flagged'] = true;
      }
      await dispatch(sendResult({
        resultId,
        data: nextResultData,
      })).unwrap()
        .catch((err) => {
          doRedirect = false;
          togglePostQuizDialog = null;
          if (err.status === 409 && err.result) {
            StorageService.setNonce(err.result.nonce)
            navigateTo(`already-passed`, { email: registerData.email });
          }
        });
      if ( doRedirect ) navigateTo('post-quiz');
      if ( togglePostQuizDialog ) togglePostQuizDialog();
      setLoading(false);
      if ( analyticData ){
        await fetchWithAutoRetry(dispatchUpdateAnalyticsData, {
          ...analyticData,
          result: resultId,
          type: quiz.type,
          quizNameSlug: quiz.quizName
        });
      }
    } catch(error){} finally {
      setLoading(false);
    }
  }

  const isPollQuiz = quiz.type === QuizTypes.Poll;

  if ( !resultFetched && (!quiz.registrationPage || quiz.registrationPage?.showPriorQuiz === false) ) return null;
  return (
    <Fragment>
      <PublicDevicePlayable />
      <div
        className="flex-1 flex flex-col"
        style={{ background: isPollQuiz && quiz.backgroundImage ? `url('${quiz.backgroundImage}') top / cover` : '' }}
      >
        <AppBar />
        {isPollQuiz ? <div className="h-24" /> : null}
        <div className={`flex-1 ${isPollQuiz ? 'pb-4' : 'py-2'}`}>
          {!!quiz.registrationPage && !quiz.registrationPage.anonymous ? (
            <PublicForm onAsyncRegisterUser={asyncRegisterUser} loading={loading} />
          ) : (
            <AnonymousForm onAsyncRegisterUser={asyncRegisterUser} loading={loading} />
          )}
        </div>
        <PoweredBy />
        {!isPollQuiz ? <BannerImage /> : null}
      </div>
    </Fragment>
  )
}

export default RegisterPage;
